import Header from '@custom/Header/Header'
import React, { useEffect, useRef, useState, useMemo } from 'react';
import './index.css';
import { useTheme } from '../../ui/src/contextProviders'
import { customStyle } from '@custom/Utils/utils';
import OpenSeadragon from "openseadragon";
import { createOSDAnnotator } from "@annotorious/openseadragon";
import "@annotorious/openseadragon/annotorious-openseadragon.css";
import Control from './Control';
import './Plugins/Filter/Filter.js';
import './Plugins/Magnifier/Magnifier'
import html2canvas from 'html2canvas';
import { useLocation } from 'react-router-dom';
import { getAllReportTemplates, getCurrentReport } from '@custom/Apis/apis';
import ReportLayout from '../../../extensions/default/src/ReportViewer';
import patientDetails from '@custom/rtk-apis/patients/patientDetails';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';
import getPathologyAIResults from '@custom/rtk-apis/microscopy/getPathologyAIResults';
import saveMicroscopyMeasurements from '@custom/rtk-apis/microscopy/saveMeasurements';
import getMicroscopyMeasurements from '@custom/rtk-apis/microscopy/getMeasurements';
import updateMicroscopyMeasurements from '@custom/rtk-apis/microscopy/updateMeasurements';
import { Loader, SuccessModal, ConfirmationPopup } from '@ohif/ui';
import deleteMicroscopyAnnotation from '@custom/rtk-apis/microscopy/deleteAnnotation';
import { servicesManager } from '../../app/src/App';
import getPatientMicroscopyData from '@custom/rtk-apis/microscopy/getPatientMicroscopicLink';
import uploadImageMicroscopy from '@custom/rtk-apis/microscopy';

const initialFilters = {
  brightnessValue: 10,
  contrastValue: 1,
  thresholdValue: 128,
  gammaValue: 1,
  erosionValue: 3,
  dilationValue: 3,
  sharpenValue: 1,
  drawingEnabled: false,
  rectangleEnable: true,
  selectedFilters: [],
  debug: false,
  magnifierEnable: false,
}

const Microscopy = () => {
  const { isDarkMode } = useTheme();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const pathname = location.pathname;
  const pathSegments = pathname.split('/');
  const uhid = useMemo(() => pathSegments[2], [pathname]);
  const wsiId = useMemo(() => pathSegments[3], [pathname]);

  const viewerRef = useRef(null);
  const annotatorRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const magnifierRef = useRef(null);
  const [copiedSeriesIndex, setCopiedSeriesIndex] = useState<number | null>(null);
  const [tileSources, setTileSources] = useState([]);
  const [isloading, setIsloading] = useState(false);
  const [viewer, setViewer] = useState(null);

  // Image Filters
  const [filters, setFilters] = useState(initialFilters)
  const [isAnnotatorInitialized, setIsAnnotatorInitialized] = useState(false);
  const [savedAnnotations, setSavedAnnotations] = useState([]);
  const [annotationModal, setAnnotationModal] = useState(false);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [width, setWidth] = useState(1116);
  const [templateList, setTemplateList] = useState(null);
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [currentReport, setCurrentReport] = useState([]);
  const [reportId, setReportId] = useState('');
  const [userAnnotating, setUserAnnotating] = useState(false);
  const [clickedAnnotation, setClickedAnnotation] = useState();
  const [popUpmsg, setPopUpmsg] = useState(null);
  const [imageBlob, setImageBlob] = useState(null);
  const [actionType, setActionType] = useState('');
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: null
  })
  const [unsavedAnnotations, setUnsavedAnnotations] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [isAIResultsOpen, setIsAIResultsOpen] = useState(false);

  const aiResultsData    = useSelector((state: RootState) => (state as any)?.pathologyAIResultsResponse?.result);
  const aiResultsLoading = useSelector((state: RootState) => (state as any)?.pathologyAIResultsResponse?.loading);

  const { uiNotificationService } = servicesManager.services;

  const getMicroscopyLink = async () => {
    setIsloading(true);
    try {
      const formData = new FormData();
      formData.append('uhid', uhid);
      const response = await dispatch(getPatientMicroscopyData(formData)).unwrap();
      if (response?.status === "ERROR") {
        uiNotificationService.show({
          title: "Error",
          message: response?.msg,
          type: "error",
          duration: 2000,
        });
        setIsloading(false);
        return;
      }
      const url = response.result.find(item => item.id === wsiId)?.url;
      setTileSources(url);
      setIsloading(false);
    } catch (err) {
      console.error("Error fetching microscopy link:", err);
    }
  };

  const handleViewAIResults = () => {
    // Fetch if: no data, different wsiId cached (user navigated to another study), or still PENDING
    // Same wsiId + COMPLETED/ERROR are final — safe to reuse without re-calling the API
    if (!aiResultsData || aiResultsData.wsiId !== wsiId || aiResultsData.status === 'PENDING') {
      const hospitalId = sessionStorage.getItem('hospitalId') || localStorage.getItem('hospitalId');
      dispatch(getPathologyAIResults({ hospitalId, wsiId }));
    }
    setIsAIResultsOpen(true);
  };

  useEffect(() => {
    getMicroscopyLink();
  }, []);


  useEffect(() => {
    if (!tileSources || tileSources.length === 0) return;


    const viewer = OpenSeadragon({
      element: viewerRef.current,
      prefixUrl: 'https://cdnjs.cloudflare.com/ajax/libs/openseadragon/2.4.2/images/',
      tileSources: tileSources.map(source => {
        if (source.endsWith('.dzi')) {
          return source;
        } else if (source.endsWith('.mp4')) {
          return {
            type: 'legacy-image-pyramid',
            levels: [
              {
                url: source,
                width: 4000,
                height: 3000,
              },
            ],
          };
        }
        return source;
      }),
      sequenceMode: true,
      showReferenceStrip: false,
      showNavigator: true,
      navigatorPosition: "ABSOLUTE",
      navigatorTop: "40px",
      navigatorLeft: "40px",
      navigatorHeight: "120px",
      navigatorWidth: "145px",
      debugMode: filters?.debug,
      zoomPerClick: 1,
      degrees: 0,
      showRotationControl: true,
      gestureSettingsTouch: {
        pinchRotate: true,
      },
      showFlipControl: true,
      constrainDuringPan: true,
      crossOriginPolicy: "Anonymous",
    });
    setViewer(viewer);

    const annotator = createOSDAnnotator(viewer, {
      drawingEnabled: filters?.drawingEnabled,
      style: {
        fill: "#ff0000",
        fillOpacity: 0.25,
        stroke: "#ff0000",
      },
    });
    annotatorRef.current = annotator;
    setIsAnnotatorInitialized(true);
    annotatorRef.current.on("updateAnnotation", (updated, annotation) => {
      console.log("update annotation: ", updated, annotation);
    });
    annotatorRef.current.on("clickAnnotation", (annotation) => {
      setClickedAnnotation(annotation);
    });

    // Initialize magnifier once during viewer setup
    const magnifier = viewer.magnifier({
      sizeRatio: 0.4,
      magnifierRotate: true,
      keyboardShortcut: 'm',
    });
    magnifierRef.current = magnifier;

    // Set initial visibility based on filters.magnifierEnable
    if (!filters.magnifierEnable) {
      magnifier.toggleVisibility(); // Hide initially if not enabled
    }

    const setupVideoOverlay = (source) => {
      const videoWidth = 4000;
      const videoHeight = 3000;
      const x = 0; // Centered at origin
      const y = 0;

      const videoElement = document.createElement('video');
      videoElement.src = source;
      videoElement.crossOrigin = "anonymous";
      videoElement.style.position = 'absolute';
      videoElement.style.width = `${videoWidth}px`;
      videoElement.style.height = `${videoHeight}px`;
      videoElement.style.zIndex = '10'; // Ensure video stays above other elements
      videoElement.muted = true;
      videoElement.loop = true;
      videoElement.controls = true;

      // Add the overlay
      const overlayRect = viewer.viewport.imageToViewportRectangle(x, y, videoWidth, videoHeight);
      viewer.addOverlay(videoElement, overlayRect);
      videoRef.current = videoElement;

      // Play the video
      const playVideo = () => {
        videoElement.play()
          .then(() => {
            console.log("Video playback started successfully");
          })
          .catch(err => {
            console.error("Error playing video:", err);
            if (err.name === "NotAllowedError") {
              console.log("Autoplay blocked. User interaction required.");
            }
          });
      };

      playVideo();

      // Ensure controls remain functional
      videoElement.addEventListener('play', () => {
        if (videoElement.paused) {
          playVideo();
        }
      });

      // Update overlay position without disrupting controls
      viewer.addHandler('animation', () => {
        const viewportRect = viewer.viewport.imageToViewportRectangle(x, y, videoWidth, videoHeight);
        viewer.updateOverlay(videoElement, viewportRect);
        // Ensure controls stay visible
        videoElement.style.zIndex = '10';
      });

      // Log when overlay is updated to debug disappearance
      viewer.addHandler('update-viewport', () => {
        console.log("Viewport updated, checking video controls visibility");
        if (!videoElement.controls) {
          console.warn("Video controls missing after update");
          videoElement.controls = true; // Attempt to restore controls
        }
      });
    };
    const handleMediaSwitch = (currentSource) => {
      if (currentSource.endsWith('.mp4')) {
        // Disable annotations for video
        annotatorRef.current.setDrawingEnabled(false);
        annotatorRef.current.clearAnnotations(); // Clear any existing annotations
        setFilters(prev => ({ ...prev, drawingEnabled: false }));
        viewer.setMouseNavEnabled(true);
        viewer.panHorizontal = true;
        viewer.panVertical = true;
        viewer.navigator.element.style.display = 'none';
        // Hide magnifier when switching to video
        if (magnifierRef.current && magnifierRef.current.element.style.display !== 'none') {
          magnifierRef.current.toggleVisibility(); // Hide magnifier
        }
        setupVideoOverlay(currentSource);
      } else if (currentSource.endsWith('.dzi')) {
        setFilters(initialFilters); // Reset filters to initial state
        // Enable annotations for .dzi
        viewer.navigator.element.style.display = 'block';
        annotatorRef.current.setDrawingEnabled(filters.drawingEnabled); // Restore based on filter state
        applyAnnotations(); // Reapply annotations for the current .dzi page
        if (videoRef.current) {
          videoRef.current.pause();
          viewer.removeOverlay(videoRef.current);
          videoRef.current = null;
        }
        // Restore magnifier visibility only if it was enabled in filters
        if (magnifierRef.current && filters.magnifierEnable && magnifierRef.current.element.style.display === 'none') {
          magnifierRef.current.toggleVisibility(); // Show magnifier if enabled
        }
      }
    };
    viewer.addHandler('open', () => {
      const currentIndex = viewer.currentPage();
      setCurrentPage(currentIndex);
      const currentSource = tileSources[currentIndex];
      handleMediaSwitch(currentSource);
    });

    viewer.addHandler('page', () => {
      if (videoRef.current) {
        videoRef.current.pause();
        viewer.removeOverlay(videoRef.current);
        videoRef.current = null;
      }
      const currentIndex = viewer.currentPage();
      setCurrentPage(currentIndex);
      const currentSource = tileSources[currentIndex];
      handleMediaSwitch(currentSource);
    });

    // Keyboard navigation functionality
    const handleKeyDown = (event) => {
      const panDistance = 0.1;
      switch (event.key) {
        case "ArrowRight": // Next Page
          if (viewer.currentPage() < tileSources.length - 1) {
            viewer.goToPage(viewer.currentPage() + 1);
          }
          break;

        case "ArrowLeft": // Previous Page
          if (viewer.currentPage() > 0) {
            viewer.goToPage(viewer.currentPage() - 1);
          }
          break;

        case "ArrowUp": // Zoom In
          viewer.viewport.zoomBy(1.2); // Increase zoom by 20%
          viewer.viewport.applyConstraints();
          break;

        case "ArrowDown": // Zoom Out
          viewer.viewport.zoomBy(0.8); // Decrease zoom by 20%
          viewer.viewport.applyConstraints();
          break;

        // case "w": // Pan Up
        // case "W":
        //   viewer.viewport.panBy(new OpenSeadragon.Point(0, -panDistance));
        //   viewer.viewport.applyConstraints();
        //   break;

        // case "s": // Pan Down
        // case "S":
        //   viewer.viewport.panBy(new OpenSeadragon.Point(0, panDistance));
        //   viewer.viewport.applyConstraints();
        //   break;

        // case "a": // Pan Left
        // case "A":
        //   viewer.viewport.panBy(new OpenSeadragon.Point(-panDistance, 0));
        //   viewer.viewport.applyConstraints();
        //   break;

        // case "d": // Pan Right
        // case "D":
        //   viewer.viewport.panBy(new OpenSeadragon.Point(panDistance, 0));
        //   viewer.viewport.applyConstraints();
        //   break;

        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      if (videoRef.current) {
        videoRef.current.pause();
      }
      annotator.destroy();
      viewer.destroy();
    };
  }, [tileSources]);

  useEffect(() => {
    if (viewer) {
      loadAnnotations();
    }
  }, [viewer, wsiId]);

  useEffect(() => {
    // //Use to track the current display image tileSource[index]
    if (viewer) {
      // Track rendered tile URLs
      viewer.addHandler("tile-drawn", function (event) {
        const currentIndex = viewer.currentPage();
        setCurrentPage(currentIndex);
      });
    }
  }, [viewer])

  useEffect(() => {
    if (viewer) {
      const appliedFilters = [];

      if (filters?.selectedFilters.includes("brightness")) {
        appliedFilters.push({
          processors: OpenSeadragon.Filters.BRIGHTNESS(filters?.brightnessValue),
        });
      }

      if (filters?.selectedFilters.includes("contrast")) {
        appliedFilters.push({
          processors: OpenSeadragon.Filters.CONTRAST(filters?.contrastValue),
        });
      }

      if (filters?.selectedFilters.includes("threshold")) {
        appliedFilters.push({
          processors: OpenSeadragon.Filters.THRESHOLDING(filters?.thresholdValue),
        });
      }

      if (filters?.selectedFilters.includes("gamma")) {
        appliedFilters.push({
          processors: OpenSeadragon.Filters.GAMMA(filters?.gammaValue), // Assuming GAMMA is defined in your OpenSeadragon Filters
        });
      }

      // Apply new filters
      if (filters?.selectedFilters.includes("greyscale")) {
        appliedFilters.push({
          processors: OpenSeadragon.Filters.GREYSCALE(), // Call GREYSCALE filter
        });
      }

      if (filters?.selectedFilters.includes("invert")) {
        appliedFilters.push({
          processors: OpenSeadragon.Filters.INVERT(), // Call INVERT filter
        });
      }
      if (filters?.selectedFilters.includes("erosion")) {
        if (filters?.erosionValue !== 3) {
          appliedFilters.push({
            processors: OpenSeadragon.Filters.MORPHOLOGICAL_OPERATION(
              filters?.erosionValue,
              Math.min
            ),
          });
        }
      }

      if (filters?.selectedFilters.includes("dilation")) {
        if (filters?.dilationValue !== 3) {
          appliedFilters.push({
            processors: OpenSeadragon.Filters.MORPHOLOGICAL_OPERATION(
              filters?.dilationValue,
              Math.min
            ),
          });
        }
      }

      if (filters?.selectedFilters.includes("sharpen")) {
        appliedFilters.push({
          processors: OpenSeadragon.Filters.CONVOLUTION([
            0, -1, 0, -1, 5, -1, 0, -1, 0,
          ]),
        });
      }

      viewer.setFilterOptions({
        filters: appliedFilters,
        loadMode: "sync",
      });
    }
  }, [
    filters?.brightnessValue,
    filters?.contrastValue,
    filters?.thresholdValue,
    filters?.gammaValue,
    filters?.erosionValue,
    filters?.dilationValue,
    filters?.sharpenValue,
    filters?.selectedFilters,
    viewer,
  ]);


  const AddToSeriesConfirmation = async () => {
    setConfirmationModal({
      isOpen: true,
      title: "Add to Series",
      message: "Are you sure want to add current frame to series ?",
      onConfirm: async () => {
        setConfirmationModal((prev) => ({ ...prev, isOpen: false }));
        await AddToSeries();
      }
    })
  }
  const AddToSeries = async () => {
    setIsloading(true);
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas) {
      console.error("Video or canvas not available for screenshot");
      uiNotificationService.show({
        title: "Error",
        message: "Video or canvas not available.",
        type: "error",
      });
      setIsloading(false);
      return;
    }

    const scale = 15;
    canvas.width = video.videoWidth * scale;
    canvas.height = video.videoHeight * scale;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(async (blob) => {
      if (!blob) {
        console.error("Failed to capture image");
        uiNotificationService.show({
          title: "Something went wrong.",
          message: "Failed to capture image",
          type: "error",
          duration: 2000,
        });
        setIsloading(false);
        return;
      }

      const file = new File([blob], "screenshot.png", { type: "image/png" });

      const urlParts = window.location.href.split("/");
      const uhid = urlParts[urlParts.length - 2];
      const wsiId = urlParts[urlParts.length - 1];

      const formData = new FormData();
      formData.append("uhid", uhid);
      formData.append("wsiId", wsiId);
      formData.append("file", file);

      try {
        const response = await dispatch(uploadImageMicroscopy(formData));
        setIsloading(false);

        if (response?.payload?.status === "SUCCESS") {
          setAnnotationModal(true);
          setPopUpmsg(response?.payload?.msg);
          setTimeout(() => {
            getMicroscopyLink();
          }, 1000);
        } else {
          uiNotificationService.show({
            title: "Something went wrong.",
            message: response?.payload?.msg || "Something went wrong while uploading...",
            type: "error",
            duration: 2000,
          });
        }
      } catch (error) {
        console.error("Upload failed:", error);
        uiNotificationService.show({
          title: "Something went wrong.",
          message: error?.msg || "Something went wrong while uploading...",
          type: "error",
          duration: 2000,
        });
        setIsloading(false);
      }
    }, "image/png");
  };

  const enableRectangleMode = () => {
    setFilters((prev) => ({
      ...prev,
      rectangleEnable: true
    }))
    annotatorRef.current.setDrawingTool("rectangle");
  };

  const enablePolygonMode = () => {
    setFilters((prev) => ({
      ...prev,
      rectangleEnable: false
    }))
    annotatorRef.current.setDrawingTool("polygon");
  };

  const toggleMode = () => {
    const newDrawingEnabled = !filters?.drawingEnabled;
    setFilters((prev) => ({
      ...prev,
      drawingEnabled: newDrawingEnabled
    }))
    if (newDrawingEnabled) {
      makeAnnotations();
    }
    // Enable or disable drawing in the annotator
    annotatorRef.current.setDrawingEnabled(newDrawingEnabled);
    if (viewer) {
      viewer.setMouseNavEnabled(!newDrawingEnabled);
      viewer.panHorizontal = !newDrawingEnabled;
      viewer.panVertical = !newDrawingEnabled;
    }
  };

  const toggleFilter = (filter) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      selectedFilters: prevFilters.selectedFilters.includes(filter)
        ? prevFilters.selectedFilters.filter((f) => f !== filter)
        : [...prevFilters.selectedFilters, filter],
    }));
  };

  useEffect(() => {
    if (viewer) {
      viewer.setDebugMode(filters?.debug); // Dynamically update debug mode
    }
  }, [viewer, filters?.debug]);

  const toggleMagnifier = () => {
    setFilters(prev => {
      const newMagnifierState = !prev.magnifierEnable;
      if (magnifierRef.current) {
        magnifierRef.current.toggleVisibility(); // Use plugin's toggleVisibility
      }
      return { ...prev, magnifierEnable: newMagnifierState };
    });
  };

  // Function to toggle debug mode
  const toggleDebug = () => {
    setFilters((prev) => ({
      ...prev,
      debug: !prev.debug,
    }));
  };

  const applyAnnotations = () => {
    if (!isAnnotatorInitialized) {
      console.warn("Annotator is not yet initialized.");
      return;
    }
    // Clear existing annotations to prevent duplicates
    annotatorRef.current.clearAnnotations();
    console.log("Applying Annotations:", savedAnnotations);
    if (savedAnnotations.length > 0) {
      const currentIndex = viewer.currentPage();
      const currentImageUrl = viewer.tileSources[currentIndex];
      const savedAnnotationsForPage = savedAnnotations.filter(
        annotation => annotation.imageUrl === currentImageUrl
      );
      savedAnnotationsForPage.forEach((annotation) => {
        annotatorRef.current.addAnnotation(annotation);
      });
      annotatorRef.current.on('selectionChanged', (annotations) => {
        if (annotations.length > 0) {
          setUserAnnotating(true);
        }
      });
    }
  };
  const makeAnnotations = () => {
    if (!isAnnotatorInitialized) {
      console.warn("Annotator is not yet initialized.");
      return;
    }
    annotatorRef.current.on("createAnnotation", (annotation) => {
      if (savedAnnotations.length === 0) {
        setUserAnnotating(true);
      }
      // Add to unsavedAnnotations for the current page
      const currentIndex = viewer.currentPage();
      const currentImageUrl = viewer.tileSources[currentIndex];
      annotation.imageUrl = currentImageUrl;
      setUnsavedAnnotations(prev =>{
        const updatedAnnotations = prev.filter(a => a.id !== annotation.id); // Remove any existing annotation with the same id
        return [...updatedAnnotations, annotation]; // Add the new annotation
      });
    });

    annotatorRef.current.on("updateAnnotation", (updated) => {
      if (savedAnnotations.length === 0) {
        setUserAnnotating(true);
      }
      const currentIndex = viewer.currentPage();
      const currentImageUrl = viewer.tileSources[currentIndex];
      updated.imageUrl = currentImageUrl;
      setUnsavedAnnotations(prev =>
        prev.map(a => (a.id === updated.id ? updated : a))
      );
    });
  }

  const removeDuplicateAnnotations = (annotations) => {
    // Create a Map to remove duplicates based on the `id` field
    const uniqueAnnotationsMap = new Map(annotations.map(annotation => [annotation.id, annotation]));

    // Convert the Map back to an array
    const uniqueAnnotations = Array.from(uniqueAnnotationsMap.values());

    return uniqueAnnotations;
  };

  const saveMeasurements = async () => {
    const currentIndex = viewer.currentPage();
    const currentImageUrl = viewer.tileSources[currentIndex];

    // Get the annotations for the current page
    const currentAnnotations = annotatorRef.current.getAnnotations();

    // Add the `imageUrl` field to the current annotations
    const currentAnnotationsWithUrl = currentAnnotations.map(annotation => ({
      ...annotation,
      imageUrl: currentImageUrl, // Include the image URL for correlation
    }));

    // Combine current annotations with previously saved annotations
    const allAnnotations = [...savedAnnotations, ...currentAnnotationsWithUrl];
    // Remove duplicate annotations based on the `id` field
    const uniqueAnnotations = removeDuplicateAnnotations(allAnnotations);

    // Prepare the payload with all annotations
    const obj = {
      uhid: uhid,
      wsiId: wsiId,
      annotation: uniqueAnnotations, // Send all annotations (new and previously saved)
    };

    // Check if there are any saved annotations
    if (savedAnnotations.length > 0 ) {
      // If annotations exist, update them
      const response = await dispatch(updateMicroscopyMeasurements(obj))
        .unwrap()
      if (response?.status === "SUCCESS") {
        setPopUpmsg('Annotation updated successfully.');
        setUserAnnotating(false);
        setAnnotationModal(true);

        // Update the savedAnnotations state with the new annotations
        setSavedAnnotations(uniqueAnnotations);
        setUnsavedAnnotations((prev) => [...prev.filter(annotation => annotation.imageUrl !== currentImageUrl)]);
      } else {
        uiNotificationService.show({
          title: "Something went wrong.",
          message: response?.msg || "Something went wrong while updating...",
          type: "error",
          duration: 2000,
        });
      }
    } else {
      // If no annotations exist, save them
      const rep = await dispatch(saveMicroscopyMeasurements(obj))
        .unwrap()
      if (rep?.status === "SUCCESS") {
        setPopUpmsg('Annotation saved successfully.');
        setAnnotationModal(true);
        setUserAnnotating(false);
        setUnsavedAnnotations((prev) => [...prev.filter(annotation => annotation.imageUrl !== currentImageUrl)]);
        // Update the savedAnnotations state with the new annotations
        setSavedAnnotations(uniqueAnnotations);

      } else {
        uiNotificationService.show({
          title: "Something went wrong.",
          message: rep?.msg || "Something went wrong while saving...",
          type: "error",
          duration: 2000,
        });
      }
    }
  };
  const loadAnnotations = async () => {

    if (!wsiId) {
      console.error("Missing wsiId, cannot load annotations.");
      return;
    }
    const currentIndex = viewer.currentPage();
    const currentImageUrl = viewer.tileSources[currentIndex];
    const obj = {
      wsiId
    }
    const response = await dispatch(getMicroscopyMeasurements(obj))
      .unwrap()

    // if (response?.status === "ERROR") {
    //   uiNotificationService.show({
    //     title: "Error",
    //     message: response?.msg,
    //     type: "error",
    //     duration: 2000,
    //   });
    //   return;
    // }

    if (response?.result?.[0]?.annotation?.length > 0) {
      setSavedAnnotations(response?.result[0]?.annotation);
      const filteredAnnotations = unsavedAnnotations.filter(annotation => annotation.imageUrl !== currentImageUrl)
      console.log("Filtered Annotations:", filteredAnnotations);
      setUnsavedAnnotations(filteredAnnotations);
    } else {
      setSavedAnnotations([]);
      setUnsavedAnnotations([]);
      annotatorRef.current.clearAnnotations();
    }

  };
  useEffect(() => {
    if (savedAnnotations.length > 0) {
      applyAnnotations();
    }
  }, [savedAnnotations])


  const handleDeleteAnnotation = async () => {
    if (!clickedAnnotation) {
      console.warn("No annotation selected for deletion.");
      return;
    }

    // Open the confirmation popup
    setConfirmationModal({
      isOpen: true,
      title: "Delete Annotataion",
      message: "Are you sure want to delete annotation ?",
      onConfirm: () => confirmDeleteAnnotation()
    })
  };

  const confirmDeleteAnnotation = async () => {
    try {
      const currentIndex = viewer.currentPage();
      const currentImageUrl = viewer.tileSources[currentIndex];

      // Check if the clicked annotation is unsaved
      const isUnsavedAnnotation = unsavedAnnotations.some(
        annotation => annotation.id === clickedAnnotation.id
      );

      if (isUnsavedAnnotation) {
        // If it's an unsaved annotation, remove it from unsavedAnnotations
        setUnsavedAnnotations(prev =>
          prev.filter(annotation => annotation.id !== clickedAnnotation.id)
        );

        // Remove the annotation from the UI
        annotatorRef.current.removeAnnotation(clickedAnnotation.id);
      } else {
        // If it's a saved annotation, handle it via the API
        const allAnnotations = [...savedAnnotations, ...unsavedAnnotations];
        const uniqueAnnotations = removeDuplicateAnnotations(allAnnotations);
        // Remove the clicked annotation
        let updatedAnnotations = uniqueAnnotations.filter(
          item => item.id !== clickedAnnotation.id
        );

        // Check if this was the last annotation
        const isLastAnnotation = updatedAnnotations.length === 0;

        if (isLastAnnotation) {
          // If it's the last annotation, delete it from the server
          const formData = new FormData();
          formData.append('wsiId', wsiId);
          formData.append('annotationId', clickedAnnotation.id);
         const response = await dispatch(deleteMicroscopyAnnotation(formData)).unwrap();
          if (response?.status === "ERROR") {
            uiNotificationService.show({
              title: "Error",
              message: response?.msg || "Something went wrong while deleting...",
              type: "error",
              duration: 2000,
            });
            return;
          }
          annotatorRef.current.clearAnnotations();
          setSavedAnnotations([]); // Reset saved annotations to an empty array
          setUnsavedAnnotations([]);
          updatedAnnotations = []; // Reset updated annotations to an empty array
        } else {
          // If it's not the last annotation, update the remaining annotations
          const obj = {
            uhid,
            wsiId,
            annotation: updatedAnnotations,
          };
          const response = await dispatch(updateMicroscopyMeasurements(obj)).unwrap();
          if (response?.status === "ERROR") {
            uiNotificationService.show({
              title: "Error",
              message: response?.msg,
              type: "error",
              duration: 2000,
            });
            return;
          }
        }

        // Clear the annotator and reload annotations
        annotatorRef.current.clearAnnotations();
        await loadAnnotations();

        // Update savedAnnotations
        setSavedAnnotations(updatedAnnotations);
      }

      // Show success message and reset state
      setPopUpmsg('Annotation deleted successfully.');
      setUserAnnotating(false);
      setAnnotationModal(true);
      setClickedAnnotation(null);
    } catch (err) {
      console.error("API Error while deleting/updating annotations:", err);
    } finally {
      // Close the confirmation popup
      setConfirmationModal((prev) => ({
        ...prev,
        isOpen: false
      }))
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const startX = e.clientX;
    const startWidth = width;

    /**
     * Handles the mouse move event during the report panel resize.
     *
     * @param {MouseEvent} e - The mouse event object.
     */
    const onMouseMove = (e: MouseEvent) => {
      const newWidth = startWidth - (e.clientX - startX);
      setWidth(newWidth > 0 ? newWidth : 0); // Ensure width is not negative
    };

    /**
     * Handles the mouse up event after the report panel resize is finished.
     * Removes the event listeners for mouse move and mouse up.
     */
    const onMouseUp = () => {
      // Remove the event listeners for mouse move and mouse up
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const getReportTemplates = async () => {
    try {
      const params = { search: "", modality: "SM" };
      const response = await getAllReportTemplates(params);
      setTemplateList(response?.templateList);
      if (currentReport?.templateId) {
        const matchingTemplate = response?.templateList?.find(
          template => template._id === currentReport.templateId
        );
        if (matchingTemplate) {
          setSelectedTemplate(matchingTemplate);
        }
      }
    } catch (error) {
      console.error('Error fetching report templates:', error);
    }
  };

  const getCurrReport = async () => {
    const url = window.location.href;
    const segments = new URL(url).pathname.split('/');
    const extractedValue = segments[segments.length - 1];
    const payload = { wsiID: extractedValue };

    try {
      const response = await getCurrentReport(payload);
      if (!response.ok) {
        setCurrentReport(response?.result);
      }
    } catch (error) {
      console.error('Error fetching current report:', error);
    }
  };
  useEffect(() => {
    if (viewer) {
      viewer.addHandler("page", function (event) {
        const currentIndex = viewer.currentPage();
        setCurrentPage(currentIndex);
        const currentImageUrl = viewer.tileSources[currentIndex];

        // Clear existing annotations
        annotatorRef.current.clearAnnotations();

        // Load saved annotations for the current image
        const savedAnnotationsForPage = savedAnnotations.filter(
          annotation => annotation.imageUrl === currentImageUrl
        );

        // Load unsaved annotations for the current image
        const unsavedAnnotationsForPage = unsavedAnnotations.filter(
          annotation => annotation.imageUrl === currentImageUrl
        );

        // Combine saved and unsaved annotations
        const annotationsToLoad = [...savedAnnotationsForPage, ...unsavedAnnotationsForPage];

        // Remove duplicates (if any)
        const uniqueAnnotations = removeDuplicateAnnotations(annotationsToLoad);

        // Load annotations into the annotator
        uniqueAnnotations.forEach(annotation => {
          annotatorRef.current.addAnnotation(annotation);
        });
      });
    }
  }, [viewer, savedAnnotations, unsavedAnnotations]);
  useEffect(() => {
    getCurrReport();
    getReportTemplates();
    const url = window.location.href;
    const parts = url.split('/');
    const uhid: string = parts.find(part => part.startsWith('UID'));
    uhid && dispatch(patientDetails({ uhid }));
  }, [])

  // Filter templates based on search input
  useEffect(() => {
    if (templateList && Array.isArray(templateList)) {
      const filtered = templateList.filter((template) =>
        template.templateName.toLowerCase().includes(search.toLowerCase())
      );
      setFilteredTemplates(filtered);
    }
  }, [search, templateList]);

  const handleViewReport = (item) => {
    setSelectedTemplate(item); // Store full object
    setActionType('view');
    setIsOpen(false);
    setSearch("");
    setIsReportOpen(!isReportOpen);
  }

  const handleSlelectTemplate = (template) => {
    setSelectedTemplate(template); // Store full object
    setActionType('generate');
    setIsOpen(false);
    setSearch("");
    setIsReportOpen(true);
  }

  const base64ToBlob = (base64, type = 'image/png') => {
    const prefix = 'data:image/png;base64,';
    if (base64.startsWith(prefix)) {
      base64 = base64.slice(prefix.length);
    }

    const byteCharacters = atob(base64);
    const byteNumbers = new Uint8Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    return new Blob([byteNumbers], { type });
  };

  const drawAnnotationsBeforeSave = () => {
    const annotations = annotatorRef.current.getAnnotations();
    annotatorRef.current.clearAnnotations();
    if (annotations.length > 0) {
      annotations.forEach((annotation) => {
        annotatorRef.current.addAnnotation(annotation);
      });
    }
  }
  const saveImage = () => {
    drawAnnotationsBeforeSave();
    const viewerContainer = document.getElementsByClassName("openseadragon-canvas")[0];
    if (!viewerContainer) {
      console.error("Canvas container not found");
      return;
    }
    // Use html2canvas to capture the entire container
    html2canvas(viewerContainer, {
      useCORS: true, // Enable cross-origin requests for external resources
      allowTaint: true,// Allow tainted images to render
      backgroundColor: "#000",
    }).then((canvas) => {
      // Convert the rendered canvas to a PNG image
      const imageURL = canvas.toDataURL("image/png");
      const base64Prefix = "data:image/png;base64,";

      // Remove base64 prefix and convert to Blob
      if (imageURL.startsWith(base64Prefix)) {
        const base64Data = imageURL.replace(base64Prefix, "");
        const blobImg = base64ToBlob(base64Data);
        setImageBlob(blobImg);
        console.log("blobImg", blobImg);
      }
      uiNotificationService.show({
        title: 'Attached',
        message: 'Image is attached to report.',
        type: 'success',
      });
      // const link = document.createElement("a");
      // link.href = imageURL;
      // link.download = "wsi-image.png"; // File name for download
      // link.click(); // Trigger the download
    }).catch((err) => {
      console.error("Error capturing the container:", err);
    });
  }
  const downloadImage = () => {
    const currentIndex = viewer.currentPage();
    const currentSource = tileSources[currentIndex];
    if (currentSource.endsWith('.mp4')) {
      //captureScreenshot();
    } else if (currentSource.endsWith('.dzi')) {
      drawAnnotationsBeforeSave();
      const viewerContainer = document.getElementsByClassName("openseadragon-canvas")[0];
      if (!viewerContainer) {
        console.error("Canvas container not found");
        return;
      }
      // Use html2canvas to capture the entire container
      html2canvas(viewerContainer, {
        useCORS: true, // Enable cross-origin requests for external resources
        allowTaint: true,// Allow tainted images to render
        backgroundColor: "#000",
      }).then((canvas) => {
        // Convert the rendered canvas to a PNG image
        const imageURL = canvas.toDataURL("image/png");

        const link = document.createElement("a");
        link.href = imageURL;
        link.download = "wsi-image.png"; // File name for download
        link.click(); // Trigger the download
      }).catch((err) => {
        console.error("Error capturing the container:", err);
      });
    }
  }

  return (
    <div className='page page__full' style={
      isDarkMode ? { background: '#090c29' } : customStyle.mainBackground
    }>
      <Header title={'WSI Image'} isBack={true} />
      <div className='page__body'>

        <div className="App">
          <Control
            filters={filters}
            setFilters={setFilters}
            getMicroscopyLink={getMicroscopyLink}
            enableRectangleMode={enableRectangleMode}
            enablePolygonMode={enablePolygonMode}
            toggleMode={toggleMode}
            toggleFilter={toggleFilter}
            toggleMagnifier={toggleMagnifier}
            toggleDebug={toggleDebug}
            saveMeasurements={saveMeasurements}
            currentReport={currentReport}
            handleViewReport={handleViewReport}
            setIsOpen={setIsOpen}
            isOpen={isOpen}
            selectedTemplate={selectedTemplate}
            search={search}
            setSearch={setSearch}
            filteredTemplates={filteredTemplates}
            handleSlelectTemplate={handleSlelectTemplate}
            userAnnotating={userAnnotating}
            saveImage={saveImage}
            clickedAnnotation={clickedAnnotation}
            handleDeleteAnnotation={handleDeleteAnnotation}
            downloadImage={downloadImage}
            tileSources={tileSources}
            currentPage={currentPage}
            AddToSeries={AddToSeriesConfirmation}
            onViewAIResults={handleViewAIResults}
          />
          <div className='flex'>

            <div
              id="my-openseadragon-container"
              ref={viewerRef}
              style={{ width: "100%", height: "600px", border: "1px solid #ddd", position: "relative" }}
            ></div>
            {isReportOpen &&
              <div style={{ width: `${width}px`, display: "flex", minWidth: '300px', maxWidth: '550px', height: "600px", overflow: "auto" }}>
                <div
                  className="hover:bg-primary-600 top-0 left-0 h-full w-2 cursor-ew-resize"
                  style={{ cursor: 'ew-resize' }}
                  onMouseDown={handleMouseDown}></div>
                <ReportLayout
                  selectedTempleate={selectedTemplate}
                  setSelectedTempleate={setSelectedTemplate}
                  setOpenReportPanel={setIsReportOpen}
                  selectedTemplateName={selectedTemplate?.templateName || currentReport?.reportName}
                  reportStatus={currentReport?.status}
                  reportId={currentReport?._id}
                  setReportId={setReportId}
                  isPathology={true}
                  imageBlob={imageBlob}
                  templateID={selectedTemplate?._id}
                  getCurrRep={currentReport}
                  actionType={actionType}
                />
              </div>
            }
          </div>
        </div>

      </div>
      {isAIResultsOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 8, padding: 24, minWidth: 420, maxWidth: 600, maxHeight: '80vh', overflowY: 'auto', position: 'relative' }}>
            <button onClick={() => setIsAIResultsOpen(false)} style={{ position: 'absolute', top: 12, right: 16, fontSize: 20, background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
            <h2 style={{ marginBottom: 16, fontSize: 18, fontWeight: 600 }}>AI Analysis Results</h2>

            {aiResultsLoading && <p>Loading...</p>}

            {!aiResultsLoading && !aiResultsData && (
              <p style={{ color: '#888' }}>No AI results found for this scan.</p>
            )}

            {!aiResultsLoading && aiResultsData && (
              <>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
                  <tbody>
                    {([
                      ['Sample Type',     aiResultsData.sampleType],
                      ['Sample Location', aiResultsData.sampleLocation],
                      ['Model',           aiResultsData.model || '—'],
                      ['Status',          aiResultsData.status],
                      ['Last Updated',    aiResultsData.updatedAt ? new Date(aiResultsData.updatedAt).toLocaleString() : '—'],
                    ] as [string, string][]).map(([label, value]) => (
                      <tr key={label} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '6px 8px', fontWeight: 500, color: '#555', width: '40%' }}>{label}</td>
                        <td style={{ padding: '6px 8px' }}>{value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <h3 style={{ marginBottom: 8, fontSize: 15, fontWeight: 600 }}>Results</h3>
                {aiResultsData.status === 'PENDING' && (
                  <p style={{ color: '#f59e0b' }}>Analysis is in progress...</p>
                )}
                {aiResultsData.status === 'ERROR' && (
                  <p style={{ color: '#ef4444' }}>Analysis failed. Please re-trigger from the scan list.</p>
                )}
                {aiResultsData.status === 'COMPLETED' && (
                  Array.isArray(aiResultsData.results) && aiResultsData.results.length > 0 ? (
                    aiResultsData.results.map((result: any, i: number) => {
                      const summary = result.interpretation?.summary || result.interpretation?.Summary;
                      const handleCopy = () => {
                        if (!summary) return;
                        navigator.clipboard.writeText(String(summary)).then(() => {
                          setCopiedSeriesIndex(i);
                          setTimeout(() => setCopiedSeriesIndex(null), 2000);
                        });
                      };
                      return (
                        <div key={i} style={{ background: '#f8f8f8', borderRadius: 6, padding: 12, marginBottom: 8 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <p style={{ fontWeight: 600, fontSize: 13, color: '#444', margin: 0 }}>Series {i + 1}</p>
                            {summary && (
                              <button
                                onClick={handleCopy}
                                title="Copy summary"
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 4,
                                  fontSize: 12, color: copiedSeriesIndex === i ? '#11bd05' : '#666',
                                  background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px',
                                  borderRadius: 4, transition: 'color 0.2s',
                                }}
                              >
                                {copiedSeriesIndex === i ? (
                                  <>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                    Copied!
                                  </>
                                ) : (
                                  <>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                                    Copy summary
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                          {result.interpretation && Object.entries(result.interpretation).map(([k, v]) => (
                            <div key={k} style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
                              <span style={{ fontWeight: 500, minWidth: 140 }}>{k}:</span>
                              <span>{String(v)}</span>
                            </div>
                          ))}
                        </div>
                      );
                    })
                  ) : (
                    <p style={{ color: '#888' }}>No result data available.</p>
                  )
                )}
              </>
            )}
          </div>
        </div>
      )}
      <SuccessModal
        isSuccessModalOpen={annotationModal}
        title={popUpmsg || 'Success'}
        handleCloseModal={() => setAnnotationModal(false)}
      />
      <ConfirmationPopup
        isOpen={confirmationModal?.isOpen}
        title={confirmationModal?.title}
        message={confirmationModal?.message}
        onConfirm={confirmationModal?.onConfirm}
        onCancel={() => setConfirmationModal((prev) => ({ ...prev, isOpen: false }))}
      />
      <Loader title='Loading...' isLoaderOpen={isloading} />
      <canvas ref={canvasRef} style={{ display: "none" }} />
    </div>)
}

export default Microscopy
